/**
 * Morphlex wrapper for DOM morphing
 *
 * Morphlex performs intelligent DOM updates by:
 * 1. Using longest increasing subsequence to detect element reordering
 * 2. Prioritizing moveBefore over mutations to preserve state
 * 3. Handling whole-document morphing efficiently
 */

import {
  morph as morphlexMorph,
  morphDocument as morphlexMorphDocument,
} from "morphlex";

/**
 * Morph the target element with new HTML content
 *
 * @param doc - The document to morph
 * @param target - The body element to morph
 * @param newContent - The new HTML string to apply
 * @returns true if morph succeeded, false if it failed and fell back to innerHTML
 */
export const morphBody = (
  doc: Document,
  target: Element,
  newContent: string,
): boolean => {
  try {
    const newDoc = new DOMParser().parseFromString(newContent, "text/html");
    const body = newDoc.body;

    if (!body) {
      return false;
    }

    // Use morphlex to intelligently update the DOM
    // It will:
    // - Detect element reordering using LIS algorithm
    // - Use moveBefore to preserve state (focus, listeners, form values)
    // - Only mutate elements that actually changed
    morphlexMorph(target, body);
    return true;
  } catch (error) {
    console.error("Morphing failed, falling back to innerHTML:", error);
    return false;
  }
};

export const morphDocument = (
  target: Document,
  newContent: string,
): boolean => {
  try {
    morphlexMorphDocument(target, newContent);
    return true;
  } catch (error) {
    console.error(
      "Morphing document failed, falling back to innerHTML:",
      error,
    );
    return false;
  }
};
